import { Divider, Stack } from '@mui/material';
import { findIndex } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { generatePath } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { shopGroupsApi, usersApi } from 'api';
import {
  CloseDialogResult,
  CopyTextId,
  CrudPage,
  DataGridColumnDefinition,
  DescriptionRow,
  Dialog,
  SidebarLayout,
  SidebarSize,
  dataGridColumns,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { FilterDefinitionType, QueryKey } from 'enums';
import { useMutation, usePartialQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, ShopGroup } from 'types';
import { formatUtils } from 'utils';

type ShopGroupsFilters = {
  id: string;
  name: string;
  userId: string;
};

type Props = {
  archived?: boolean;
};

export const ShopGroupsPage: React.FC<Props> = ({ archived }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.shop_groups',
  });
  const { role, isAdmin, isTechOperator } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const queryFn = useMemo(() => {
    if (archived) {
      return shopGroupsApi.getAllArchivePaginatedAsRole(role);
    }
    return shopGroupsApi.getAllPaginatedAsRole(role);
  }, [archived, role]);

  const queryResult = usePartialQuery(
    archived ? QueryKey.ShopGroupsArchive : QueryKey.ShopGroups,
    queryFn,
  );

  const { data: merchants = [] } = useQuery(
    QueryKey.UsersMerchants,
    usersApi.getAllMerchants,
  );

  const [confirmArchiveDialogProps, setConfirmArchiveDialogProps] = useState<{
    open: boolean;
    data?: ShopGroup;
  }>({
    open: false,
  });

  const { mutate: archiveGroup, isLoading: isArchiveLoading } = useMutation(
    shopGroupsApi.archiveAsRole(role),
    {
      onSuccess: () => {
        setConfirmArchiveDialogProps({ open: false });
        queryClient.invalidateQueries(QueryKey.ShopGroups);
      },
    },
  );

  const detailsPage = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.SHOP_GROUP_DETAILS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.SHOP_GROUP_DETAILS;
    }
  }, [isAdmin, isTechOperator]);

  const handleOpenDetails = useCallback(
    (id: string) => {
      navigate(generatePath(detailsPage!, { id }));
    },
    [navigate, detailsPage],
  );

  const handleCloseArchiveDialog = useCallback(
    ({ ok, data }: CloseDialogResult<ShopGroup>) => {
      if (ok && data) {
        archiveGroup(data.id);
      } else {
        setConfirmArchiveDialogProps({ open: false });
      }
    },
    [archiveGroup],
  );

  const handleArchive = useCallback((data: ShopGroup) => {
    setConfirmArchiveDialogProps({ open: true, data });
  }, []);

  const [selectedGroup, setSelectedGroup] = useState<ShopGroup | null>(null);
  const handleOpenSidebar = useCallback(
    (group: ShopGroup) => setSelectedGroup(group),
    [],
  );
  const handleCloseSidebar = useCallback(() => setSelectedGroup(null), []);

  const columns = useMemo(
    (): DataGridColumnDefinition<ShopGroup>[] => [
      {
        header: t('fields.id'),
        valueGetter: (item) => <CopyTextId id={item.id} />,
      },
      {
        header: t('fields.name'),
        valueGetter: (item) => formatUtils.formatName(item),
      },
      {
        header: t('fields.asset_currency'),
        valueGetter: (item) => item.assetCurrency?.code,
      },
      {
        header: t('fields.merchant'),
        valueGetter: (item) => item.user?.name,
      },
      {
        header: t('fields.shops'),
        multiValueRenderer: {
          itemsGetter: (item) => item.shops || [],
          valueGetter: (shop) => <div key={shop.id}>{shop.name}</div>,
        },
      },
      ...(!archived
        ? [
            dataGridColumns.getActionsColumn({
              handleEdit: (item: ShopGroup) => handleOpenDetails(item.id),
              handleRemove: handleArchive,
            }),
          ]
        : []),
    ],
    [archived, handleOpenDetails, handleArchive, t],
  );

  const filtersDefinitions: FilterDefinition<ShopGroupsFilters>[] = useMemo(
    () => [
      {
        label: t('filters.id'),
        name: 'id',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
      {
        label: t('filters.name'),
        name: 'name',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('filters.merchant'),
        name: 'userId',
        type: FilterDefinitionType.User,
        users: merchants,
      },
    ],
    [t, merchants],
  );

  const handleCreate = useCallback(() => {
    handleOpenDetails(NEW_ID);
  }, [handleOpenDetails]);

  const groups = queryResult.data?.items;

  const sidebarContent = useMemo(() => {
    if (!selectedGroup) return null;
    return (
      <Stack direction="column" spacing={6}>
        <Stack direction="column" spacing={3}>
          <DescriptionRow
            title={t('sidebar.id')}
            value={selectedGroup.id}
            canCopy
          />
          <DescriptionRow
            title={t('sidebar.merchant')}
            value={selectedGroup.user?.name}
          />
          <DescriptionRow
            title={t('sidebar.currency')}
            value={selectedGroup.assetCurrency?.code}
          />
          <DescriptionRow
            title={t('sidebar.shops')}
            value={selectedGroup.shops?.map((s) => s.name).join(', ') || '—'}
          />
        </Stack>
        <Stack direction="column" spacing={3}>
          <Divider textAlign="left">{t('sidebar.credentials')}</Divider>
          <DescriptionRow
            title={t('sidebar.api_key')}
            value={selectedGroup.apiKey}
            canCopy
          />
          <DescriptionRow
            title={t('sidebar.signature_key')}
            value={selectedGroup.signatureKey}
            canCopy
          />
        </Stack>
      </Stack>
    );
  }, [selectedGroup, t]);

  return (
    <Fragment>
      <SidebarLayout
        sidebarProps={{
          size: SidebarSize.md,
          open: !!selectedGroup,
          onClose: handleCloseSidebar,
          title: formatUtils.formatName(selectedGroup),
          content: sidebarContent,
        }}
      >
        <CrudPage
          header={{
            hide: archived,
            title: t('title'),
            ...(!archived && {
              rightContentButton: { onClick: handleCreate },
            }),
          }}
          filters={{ filtersDefinitions }}
          table={{
            queryResult,
            columns,
            paginated: true,
            onRowClick: handleOpenSidebar,
            selectedRowIndex: findIndex(groups, {
              id: selectedGroup?.id,
            }),
          }}
        />
      </SidebarLayout>
      <Dialog
        title={t('archive_dialog.title')}
        onClose={handleCloseArchiveDialog}
        disabled={isArchiveLoading}
        {...confirmArchiveDialogProps}
      >
        <Fragment>
          {confirmArchiveDialogProps.data && (
            <div>{confirmArchiveDialogProps.data.name}</div>
          )}
        </Fragment>
      </Dialog>
    </Fragment>
  );
};
